import { ArrowRight, TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../ui/card";
import { DashboardInsights } from "../../../../../../hooks/useDashboardInsights";
import { Skeleton } from "../../../../../ui/skeleton";
import { DataLabel, NoData } from "./DataLabel";

interface BeforeAfterProps {
  insights: DashboardInsights | null;
  loading: boolean;
}

export const BeforeAfter = ({ insights, loading }: BeforeAfterProps) => {
  if (loading) {
    return (
      <Card className="border-border/50">
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent><Skeleton className="h-32 w-full" /></CardContent>
      </Card>
    );
  }

  if (!insights || insights.total_reviews === 0) {
    return (
      <Card className="border-border/50 p-6">
        <NoData message="Not enough data to compare — keep collecting reviews." />
      </Card>
    );
  }

  const metrics = [
    {
      label: "Reviews",
      before: insights.before.reviews.toString(),
      after: insights.after.reviews.toString(),
      improved: insights.after.reviews > insights.before.reviews,
    },
    {
      label: "Average Rating",
      before: `${insights.before.rating}⭐`,
      after: `${insights.after.rating}⭐`,
      improved: insights.after.rating >= insights.before.rating,
    },
    {
      label: "Negative %",
      before: `${insights.before.negativePercent}%`,
      after: `${insights.after.negativePercent}%`,
      improved: insights.after.negativePercent <= insights.before.negativePercent,
    },
    {
      label: "Response Rate",
      before: `${insights.before.responseRate}%`,
      after: `${insights.after.responseRate}%`,
      improved: insights.after.responseRate >= insights.before.responseRate,
    },
  ];

  return (
    <Card className="border-border/50 transition-shadow hover:shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            Your Progress: Then vs Now
          </CardTitle>
          <DataLabel type="derived" />
        </div>
        <p className="text-xs text-muted-foreground">First 30 days compared to last 30 days</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-4">
          {metrics.map((m) => (
            <div key={m.label} className="text-center space-y-3">
              <p className="text-xs font-medium text-muted-foreground">{m.label}</p>
              <div className="flex items-center justify-center gap-2">
                <div className="rounded-lg bg-muted/80 px-3 py-2">
                  <p className="text-sm font-semibold text-muted-foreground">{m.before}</p>
                  <p className="text-[10px] text-muted-foreground">Before</p>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className={`rounded-lg px-3 py-2 ${m.improved ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-red-50 dark:bg-red-950/30"}`}>
                  <p className={`text-sm font-semibold ${m.improved ? "text-emerald-600" : "text-red-500"}`}>{m.after}</p>
                  <p className="text-[10px] text-muted-foreground">Now</p>
                </div>
              </div>
              {m.improved ? (
                <TrendingUp className="mx-auto h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <TrendingDown className="mx-auto h-3.5 w-3.5 text-red-400" />
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
